import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen, Bot, CalendarDays, ChartNoAxesCombined, ChevronLeft, ChevronRight, ClipboardCheck,
  FileStack, Home, Library, LogOut, Menu, MessageSquareText, NotebookTabs, PanelRightClose,
  PanelRightOpen, Settings, ShieldCheck, UserRound, X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { requireSupabase } from '../lib/supabase';

const nav = [
  { to: '/hub', label: 'Home', icon: Home, end: true },
  { to: '/hub/rebuilds', label: 'Rebuilds', icon: BookOpen },
  { to: '/hub/workbook', label: 'Workbook', icon: NotebookTabs },
  { to: '/hub/blueprint', label: 'Blueprint', icon: FileStack },
  { to: '/hub/scorecard', label: 'Scorecard', icon: ChartNoAxesCombined },
  { to: '/hub/rebuild-map', label: 'Rebuild Map', icon: CalendarDays },
  { to: '/hub/delphi', label: 'Delphi', icon: Bot },
  { to: '/hub/calls', label: 'Calls', icon: ClipboardCheck },
  { to: '/hub/community', label: 'Community', icon: MessageSquareText },
  { to: '/hub/library', label: 'Library', icon: Library },
  { to: '/hub/profile', label: 'Profile', icon: UserRound },
];

export function HubShell() {
  const { profile, roles, signOut } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = roles.includes('admin') || roles.includes('super_admin');

  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    requireSupabase().from('user_preferences').select('sidebar_collapsed,right_panel_open').maybeSingle()
      .then(({ data }) => {
        if (data) {
          setCollapsed(Boolean(data.sidebar_collapsed));
          setRightPanelOpen(Boolean(data.right_panel_open));
        }
      });
  }, []);

  const persistShell = async (nextCollapsed: boolean, nextRight: boolean) => {
    setCollapsed(nextCollapsed);
    setRightPanelOpen(nextRight);
    await requireSupabase().from('user_preferences').upsert({
      user_id: profile?.id,
      sidebar_collapsed: nextCollapsed,
      right_panel_open: nextRight,
    });
  };

  const initials = useMemo(() => {
    const source = profile?.full_name || profile?.email || 'Member';
    return source.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
  }, [profile]);

  return (
    <div className={`hub-app ${collapsed ? 'sidebar-collapsed' : ''} ${rightPanelOpen ? 'right-open' : ''}`}>
      <header className="topbar">
        <button className="icon-button mobile-only" onClick={() => setMobileOpen(true)} aria-label="Open navigation"><Menu /></button>
        <button className="icon-button desktop-only" onClick={() => persistShell(!collapsed, rightPanelOpen)} aria-label="Toggle navigation">
          {collapsed ? <ChevronRight /> : <ChevronLeft />}
        </button>
        <Link to="/hub" className="top-brand">
          <span className="brand-mark">TRP</span>
          <span><b>The Rebuild Hub</b><small>One Hub · Four Rebuilds</small></span>
        </Link>
        <div className="topbar-actions">
          <button className="icon-button" onClick={() => persistShell(collapsed, !rightPanelOpen)} aria-label="Toggle context panel">
            {rightPanelOpen ? <PanelRightClose /> : <PanelRightOpen />}
          </button>
          <button className="profile-button" onClick={() => navigate('/hub/profile')}>
            <span>{initials || 'M'}</span><b>{profile?.full_name || 'Member'}</b>
          </button>
        </div>
      </header>

      <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="mobile-sidebar-head mobile-only"><span>Navigation</span><button className="icon-button" onClick={() => setMobileOpen(false)}><X /></button></div>
        <nav>
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Icon size={19} /><span>{label}</span>
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink to="/hub/admin" className={({ isActive }) => `sidebar-link admin-link ${isActive ? 'active' : ''}`}>
              <ShieldCheck size={19} /><span>Admin</span>
            </NavLink>
          )}
        </nav>
        <div className="sidebar-footer">
          <button className="sidebar-link" onClick={async () => { await signOut(); navigate('/hub/login'); }}>
            <LogOut size={19} /><span>Sign out</span>
          </button>
        </div>
      </aside>
      {mobileOpen && <button className="mobile-backdrop" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />}

      <main className="hub-main"><Outlet /></main>

      <aside className="context-panel">
        <p className="eyebrow">Your next move</p>
        <h3>Start where you are.</h3>
        <p>The hub keeps the work visible. Phase 2 connects the course player, progress, notes, and protected content.</p>
        <Link className="button primary full" to="/hub/rebuilds">View your rebuilds</Link>
        <div className="context-rule" />
        <p className="eyebrow">Platform status</p>
        <div className="mini-status"><span className="status-dot active" />Authentication connected</div>
        <div className="mini-status"><span className="status-dot active" />Entitlement-aware routes</div>
        <div className="mini-status"><span className="status-dot active" />Course player and progress live</div>
      </aside>

      <nav className="mobile-bottom-nav">
        {nav.slice(0, 5).map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => isActive ? 'active' : ''}><Icon /><span>{label}</span></NavLink>
        ))}
      </nav>
    </div>
  );
}
